import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import "./Contract.css";

function Contract() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [toast, setToast] = useState(null);

  const [contract, setContract] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchContract = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/contract`);
      const data = await res.json();
      setContract(data.contract || null);
    } catch {
      showToast("Failed to load contract", true);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      showToast("Please select a PDF file", true);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("contract", file);
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/contract`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Upload failed");
      }
      showToast("Contract uploaded!");
      await fetchContract();
    } catch (err) {
      showToast(err.message || "Failed to upload contract", true);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleCopyLink = async () => {
    setCopying(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/contract/link`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to get link");
      await navigator.clipboard.writeText(data.url);
      showToast("Link copied to clipboard!");
    } catch (err) {
      showToast(err.message || "Failed to copy link", true);
    } finally {
      setCopying(false);
    }
  };

  const handleSend = async () => {
    if (!phoneNumber.trim()) {
      showToast("Enter the client's WhatsApp number", true);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/contract/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: phoneNumber.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to send");
      showToast("Contract sent to client!");
    } catch (err) {
      showToast(err.message || "Failed to send contract", true);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="ctr-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="ctr-main-content"><div className="ctr-loading">Loading contract...</div></div>
      </div>
    );
  }

  return (
    <div className="ctr-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {toast && <div className={`ctr-toast${toast.isError ? " ctr-toast--error" : ""}`}>{toast.msg}</div>}

      <div className="ctr-main-content">
        <header className="ctr-header">
          <div className="ctr-header-left">
            <button className="ctr-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="ctr-page-title">Contract</h1>
          </div>
          <div className="ctr-header-right">
            <button className="ctr-btn ctr-btn--outline" onClick={() => navigate(`/events/${eventId}`)}>
              ← Back to Event
            </button>
          </div>
        </header>

        <main className="ctr-main">
          {!contract && (
            <div className="ctr-card">
              <h3>Upload a contract</h3>
              <p className="ctr-hint">
                Upload your studio's contract as a PDF. Leave blank space at the bottom of the last page —
                that's where the client's signature will be placed once signed.
              </p>
              <label className="ctr-upload-btn">
                {uploading ? "Uploading..." : "Choose PDF file"}
                <input type="file" accept="application/pdf" onChange={handleUpload} disabled={uploading} hidden />
              </label>
            </div>
          )}

          {contract && contract.status === "pending" && (
            <>
              <div className="ctr-card">
                <div className="ctr-status ctr-status--pending">Awaiting signature</div>
                <p className="ctr-filename">{contract.original_filename}</p>
                <a className="ctr-link" href={contract.pdf_url} target="_blank" rel="noopener noreferrer">
                  View contract PDF ↗
                </a>
                <label className="ctr-upload-btn ctr-upload-btn--sm">
                  {uploading ? "Uploading..." : "Replace file"}
                  <input type="file" accept="application/pdf" onChange={handleUpload} disabled={uploading} hidden />
                </label>
              </div>

              <div className="ctr-card">
                <h3>Share with client</h3>
                <p className="ctr-hint">Send via WhatsApp, or copy the link to share it yourself.</p>
                <button className="ctr-btn ctr-btn--outline ctr-copy-btn" onClick={handleCopyLink} disabled={copying}>
                  {copying ? "Getting link..." : "🔗 Copy Link"}
                </button>
                <div className="ctr-send-row">
                  <input
                    type="tel"
                    className="ctr-input"
                    placeholder="Client's WhatsApp number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                  <button className="ctr-btn ctr-btn--primary" onClick={handleSend} disabled={sending}>
                    {sending ? "Sending..." : "Send via WhatsApp"}
                  </button>
                </div>
              </div>
            </>
          )}

          {contract && contract.status === "signed" && (
            <div className="ctr-card">
              <div className="ctr-status ctr-status--signed">✓ Signed</div>
              <p className="ctr-filename">{contract.original_filename}</p>
              <p className="ctr-signed-meta">
                Signed by <strong>{contract.signer_name}</strong> on{" "}
                {new Date(contract.signed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </p>
              <a
                className="ctr-btn ctr-btn--primary ctr-download-link"
                href={`${API_BASE_URL}/api/events/${eventId}/contract/signed-file`}
                target="_blank"
                rel="noopener noreferrer"
              >
                ⬇ Download Signed Contract
              </a>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default Contract;
